import { useCallback, useEffect, useRef, useState } from 'react'

// YouTube Player state interface
interface YouTubePlayerState {
  playerId: string
  player: any | null
  duration: number
  currentTime: number
  isPlaying: boolean
  isReady: boolean
  error: string | null
  togglePlayPause: () => void
  seekTo: (_time: number) => void
  pause: () => void
}

// Declare YouTube API types
declare global {
  interface Window {
    YT: any
    onYouTubeIframeAPIReady: () => void
  }
}

const waitForElement = (
  id: string,
  isLive: () => boolean,
  maxAttempts = 20,
): Promise<void> =>
  new Promise((resolve, reject) => {
    let attempts = 0
    const check = () => {
      if (!isLive()) {
        reject(new Error('Unmounted before element found'))
        return
      }
      if (document.getElementById(id)) {
        resolve()
        return
      }
      if (++attempts >= maxAttempts) {
        reject(new Error('Player mount target not found'))
        return
      }
      requestAnimationFrame(check)
    }
    check()
  })

// YouTube error codes to user-friendly messages
const getYouTubeErrorMessage = (errorCode: number): string => {
  switch (errorCode) {
    case 2:
      return 'Invalid video ID'
    case 5:
    case 101:
    case 150:
      return 'Preview unavailable for this video'
    case 100:
      return 'Video not found'
    default:
      return 'Preview unavailable'
  }
}

/**
 * Hook for managing YouTube player integration
 * Provides audio extraction and playback controls for YouTube videos
 */
export const useYouTubePlayer = (
  videoId: string | null,
): YouTubePlayerState => {
  const [player, setPlayer] = useState<any>(null)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isReady, setIsReady] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const playerRef = useRef<any>(null)
  const intervalRef = useRef<NodeJS.Timeout | null>(null)
  const apiPollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const [playerId, setPlayerId] = useState<string>(
    `youtube-player-${Date.now()}`,
  )

  useEffect(() => {
    if (!videoId) return

    // Generate a fresh mount-target ID for each video. player.destroy() removes
    // the iframe but not the original div (YouTube replaced it), so reusing the
    // same ID means the next new YT.Player() call can't find its mount target.
    const newId = `youtube-player-${Date.now()}`
    setPlayerId(newId)
    setIsReady(false)
    setPlayer(null)
    setIsPlaying(false)
    setDuration(0)
    setCurrentTime(0)
    setError(null)
    playerRef.current = null

    let isMounted = true

    const loadYouTubeAPI = (): Promise<any> => {
      return new Promise((resolve, reject) => {
        // Already fully loaded
        if (window.YT?.Player) {
          resolve(window.YT)
          return
        }

        // Add script if not already present
        if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
          const tag = document.createElement('script')
          tag.src = 'https://www.youtube.com/iframe_api'
          tag.onerror = () => reject(new Error('Failed to load YouTube API'))
          const firstScriptTag = document.getElementsByTagName('script')[0]
          if (firstScriptTag?.parentNode) {
            firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
          }
        }

        // Callback fast path - zero latency when it fires
        window.onYouTubeIframeAPIReady = () => {
          if (apiPollIntervalRef.current) {
            clearInterval(apiPollIntervalRef.current)
            apiPollIntervalRef.current = null
          }
          if (isMounted) resolve(window.YT)
        }

        // Poll as fallback (handles race if callback already fired)
        let attempts = 0
        const maxAttempts = 50 // 5 seconds total
        apiPollIntervalRef.current = setInterval(() => {
          attempts++
          if (window.YT?.Player) {
            if (apiPollIntervalRef.current) {
              clearInterval(apiPollIntervalRef.current)
              apiPollIntervalRef.current = null
            }
            if (isMounted) resolve(window.YT)
          } else if (attempts >= maxAttempts) {
            if (apiPollIntervalRef.current) {
              clearInterval(apiPollIntervalRef.current)
              apiPollIntervalRef.current = null
            }
            if (isMounted) reject(new Error('YouTube API load timeout'))
          }
        }, 100)
      })
    }

    const initPlayer = async () => {
      try {
        const YT = await loadYouTubeAPI()

        if (!isMounted) return

        await waitForElement(newId, () => isMounted)

        if (!isMounted) return

        const newPlayer = new YT.Player(newId, {
          // Minimal size since we're only using audio (safari on macOS requires a visible player for audio to work)
          height: '150',
          width: '150',
          videoId: videoId,
          playerVars: {
            autoplay: 0,
            controls: 0,
            disablekb: 1,
            fs: 0,
            modestbranding: 1,
            playsinline: 1,
            origin: window.location.origin,
          },
          events: {
            onReady: (event: any) => {
              if (!isMounted) return
              try {
                const dur = event.target.getDuration()
                setDuration(dur)
                setPlayer(event.target)
                setIsReady(true)
                playerRef.current = event.target
              } catch (err) {
                setError('Failed to initialize player')
              }
            },
            onStateChange: (event: any) => {
              if (!isMounted) return

              if (event.data === YT.PlayerState.PLAYING) {
                setIsPlaying(true)
              } else {
                setIsPlaying(false)
              }
            },
            onError: (event: any) => {
              if (!isMounted) return
              setError(getYouTubeErrorMessage(event.data))
            },
          },
        })

        playerRef.current = newPlayer
      } catch (err) {
        if (!isMounted) return
        setError(err instanceof Error ? err.message : 'Unknown error')
      }
    }

    initPlayer()

    return () => {
      isMounted = false
      if (apiPollIntervalRef.current) {
        clearInterval(apiPollIntervalRef.current)
        apiPollIntervalRef.current = null
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      if (playerRef.current && playerRef.current.destroy) {
        try {
          playerRef.current.destroy()
        } catch (_e) {
          // Ignore cleanup errors
        }
      }
    }
  }, [videoId])

  // Track playback position
  useEffect(() => {
    if (!isPlaying || !playerRef.current) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
      return
    }

    // Only start interval if we don't already have one running
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        if (playerRef.current && playerRef.current.getCurrentTime) {
          try {
            const time = playerRef.current.getCurrentTime()
            setCurrentTime(time)
          } catch (e) {
            // Ignore time update errors
          }
        }
      }, 100)
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }
  }, [isPlaying])

  const togglePlayPause = useCallback(() => {
    if (!playerRef.current) {
      return
    }

    try {
      if (isPlaying) {
        playerRef.current.pauseVideo()
      } else {
        playerRef.current.playVideo()
      }
    } catch (err) {
      console.error('[useYouTubePlayer] togglePlayPause error:', err)
    }
  }, [isPlaying])

  const seekTo = useCallback((time: number) => {
    if (!playerRef.current) {
      return
    }

    try {
      playerRef.current.seekTo(time, true)
    } catch (err) {
      console.error('[useYouTubePlayer] seekTo error:', err)
    }
  }, [])

  const pause = useCallback(() => {
    if (!playerRef.current) {
      return
    }
    try {
      playerRef.current.pauseVideo()
    } catch (err) {
      console.error('[useYouTubePlayer] Pause error:', err)
    }
  }, [])

  return {
    playerId,
    player,
    duration,
    currentTime,
    isPlaying,
    isReady,
    error,
    togglePlayPause,
    seekTo,
    pause,
  }
}
